import { ApiError } from '../api/client.ts';
import {
  startStepUp,
  verifyStepUp,
  type BiometricStart,
  type EmailStart,
  type StepUpAction,
  type StepUpParams,
} from '../api/step-up.ts';
import type { BiometricState, Platform } from '../platform/index.ts';

export const messageFor = (err: unknown, fallback: string): string => {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message !== '') return err.message;
  return fallback;
};

// What the confirmation sheet opens with, when it has to open at all.
export type SheetSeed =
  | {
      readonly kind: 'STARTED';
      readonly started: BiometricStart | EmailStart;
      readonly biometrics: BiometricState;
      readonly error?: string;
    }
  | { readonly kind: 'BLOCKED'; readonly message: string };

export type ConfirmationAttempt =
  | { readonly kind: 'GRANT'; readonly grant: string }
  | { readonly kind: 'SHEET'; readonly seed: SheetSeed };

// Tries to confirm without putting anything on screen.
//
// A set-up device already shows the user a prompt of its own, naming the
// operation — putting a "confirm it is you" sheet in front of that would be
// asking twice for the same thing. So the sheet exists for the cases where
// there is something to decide or to read: an emailed code, a device that
// failed, a device this account has not set up, or an outright refusal.
export const attemptConfirmation = async (
  action: StepUpAction,
  params: StepUpParams,
  summary: string,
  platform: Platform,
): Promise<ConfirmationAttempt> => {
  let started: Awaited<ReturnType<typeof startStepUp>>;
  let biometrics: BiometricState;
  try {
    [started, biometrics] = await Promise.all([startStepUp(action, params), platform.biometricState()]);
  } catch (err: unknown) {
    return {
      kind: 'SHEET',
      seed: { kind: 'BLOCKED', message: messageFor(err, 'Could not start confirmation.') },
    };
  }

  if (started.kind === 'GRANTED') return { kind: 'GRANT', grant: started.grant };
  if (started.kind === 'EMAIL') {
    return { kind: 'SHEET', seed: { kind: 'STARTED', started, biometrics } };
  }
  // The account has a device, but the token lives on the device that set it up
  // and cannot travel. Nothing to attempt here — the sheet explains it.
  if (!biometrics.tokenSaved) {
    return { kind: 'SHEET', seed: { kind: 'STARTED', started, biometrics } };
  }

  const result = await platform.biometricUnlock(summary);
  if (!result.ok || result.token === '') {
    platform.haptic('error');
    return {
      kind: 'SHEET',
      seed: {
        kind: 'STARTED',
        started,
        biometrics,
        error: 'That check did not pass.',
      },
    };
  }

  try {
    const granted = await verifyStepUp(started.ceremonyId, {
      biometricToken: result.token,
      deviceId: biometrics.deviceId,
    });
    platform.haptic('success');
    return { kind: 'GRANT', grant: granted.grant };
  } catch (err: unknown) {
    platform.haptic('error');
    return {
      kind: 'SHEET',
      seed: {
        kind: 'STARTED',
        started,
        biometrics,
        error: messageFor(err, 'That check did not pass.'),
      },
    };
  }
};
